import React from 'react';

import { ORAL_ARGUMENTS_SORT_OPTIONS, SEARCH_OPERATORS } from '../../constants';
import {
  SearchForm,
  type SearchFormData,
  type SearchTypeOption,
  type FilterChipConfig,
  type MoreSectionConfig,
} from '../shared';

export type OralArgumentsSearchType =
  | 'keyword'
  | 'docketNumber'
  | 'caseName'
  | 'judgeName';

export interface OralArgumentsSearchFormData
  extends Omit<SearchFormData, 'searchType'> {
  searchType: OralArgumentsSearchType;
}

export interface OralArgumentsSearchFormProps {
  onSubmit?: (data: OralArgumentsSearchFormData) => void;
  initialSearchType?: OralArgumentsSearchType;
  initialQuery?: string;
  initialExactMatch?: boolean;
  sortValue?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

const ORAL_ARGUMENTS_SEARCH_TYPE_OPTIONS: SearchTypeOption[] = [
  { value: 'keyword', label: 'Keyword', placeholder: 'Search by Keyword' },
  {
    value: 'docketNumber',
    label: 'Docket Number',
    placeholder: 'Enter docket number (e.g., docketNumber: 12345)',
    prefill: 'docketNumber:',
  },
  {
    value: 'caseName',
    label: 'Case Name',
    placeholder: 'Enter case name (e.g., caseName: Smith v. Jones)',
    prefill: 'caseName:',
  },
  {
    value: 'judgeName',
    label: 'Judge Name',
    placeholder: 'Enter judge name (e.g., judge: Smith)',
    prefill: 'judge:',
  },
];

const ORAL_ARGUMENTS_FILTER_CHIP_CONFIGS: FilterChipConfig[] = [
  {
    id: 'jurisdiction',
    label: 'Jurisdiction',
    type: 'jurisdiction',
  },
  {
    id: 'caseName',
    label: 'Name',
    type: 'text',
    fieldLabel: 'Case Name',
    placeholder: 'e.g., Smith v. Johnson',
  },
  {
    id: 'docketNumber',
    label: 'Docket',
    type: 'text',
    fieldLabel: 'Docket Number',
    placeholder: 'e.g., 21-1234',
  },
  {
    id: 'judge',
    label: 'Judge Name',
    type: 'text',
    fieldLabel: 'Judge',
    placeholder: 'e.g., John Smith',
  },
];

const ORAL_ARGUMENTS_MORE_SECTION_CONFIG: MoreSectionConfig = {
  id: 'more',
  label: 'More',
  popoverTitle: 'Add More Details',
  fields: [
    {
      id: 'orderBy',
      label: 'Search Results Order',
      type: 'select',
      options: ORAL_ARGUMENTS_SORT_OPTIONS.map((option) => ({
        value: option.value,
        label: option.display,
      })),
      defaultValue: 'score_desc',
    },
  ],
};

export const OralArgumentsSearchForm: React.FC<
  OralArgumentsSearchFormProps
> = ({
  onSubmit,
  initialSearchType = 'keyword',
  initialQuery = '',
  initialExactMatch = true,
  sortValue,
  isLoading = false,
  disabled = false,
}) => {
  const handleSubmit = (data: SearchFormData) => {
    if (onSubmit) {
      onSubmit({
        ...data,
        searchType: data.searchType as OralArgumentsSearchType,
      });
    }
  };

  const initialFilters = sortValue
    ? { moreFilters: { orderBy: sortValue } }
    : undefined;

  return (
    <SearchForm
      title="Search Oral Arguments"
      searchTypeOptions={ORAL_ARGUMENTS_SEARCH_TYPE_OPTIONS}
      filterChipConfigs={ORAL_ARGUMENTS_FILTER_CHIP_CONFIGS}
      moreSectionConfig={ORAL_ARGUMENTS_MORE_SECTION_CONFIG}
      onSubmit={handleSubmit}
      initialSearchType={initialSearchType}
      initialQuery={initialQuery}
      initialExactMatch={initialExactMatch}
      initialFilters={initialFilters}
      isLoading={isLoading}
      disabled={disabled}
      showExactMatch={true}
      operatorsData={SEARCH_OPERATORS}
      testId="oral-arguments-search-form"
    />
  );
};

export default OralArgumentsSearchForm;
